"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { MoveRight } from "lucide-react"; // 引入圖示增加動態感

export default function OurMission() {
  // 適度調高閾值，讓動畫在進入視點較深時才觸發，更具張力
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="1948" id="品牌故事" ref={ref} className="bg-secondary text-foreground py-24 md:py-40 overflow-hidden relative">
      {/* 裝飾性背景：加入視差感與漸變 */}
      <div className={cn("absolute -right-16 -top-10 text-[25rem] font-serif font-bold text-primary/5 select-none transition-all duration-[1500ms] ease-out", isVisible ? "opacity-100 translate-x-0 rotate-0" : "opacity-0 translate-x-40 rotate-12")}>
        Hent
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {/* 左側：精緻化的圖片容器 */}
          <div className={cn("relative w-full h-[550px] lg:h-[800px] transition-all duration-[1200ms] ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20")}>
            {/* 圖片後方的裝飾方塊 */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/10 -z-10" />
            
            <div className="relative w-full h-full overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618354/img/warehouse_operations_bw.png" alt="匠心木藝地板展示" className={cn("w-full h-full object-cover transition-transform duration-[4000ms] ease-out", isVisible ? "scale-105" : "scale-125")} />
              {/* 質感疊加層 */}
              <div className="absolute inset-0 bg-primary/5 mix-blend-multiply" />
              {/* 圖片上的裝飾性內邊框 */}
              <div className="absolute inset-6 border border-white/20 pointer-events-none" />
            </div>

            {/* 懸浮標籤：強調在地感或品牌年份 */}
            <div className={cn("absolute -right-8 top-12 bg-primary text-primary-foreground px-6 py-4 hidden md:block transition-all duration-1000 delay-700", isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10")}>
              <p className="text-xs tracking-[0.3em] font-sans uppercase">Since 1989</p>
              <p className="font-serif text-lg">Tainan, Taiwan</p>
            </div>
          </div>

          {/* 右側：內容排版與節奏優化 */}
          <div className="flex flex-col justify-center">
            <div className="space-y-6">
              {/* 副標題 */}
              <div className="overflow-hidden">
                <span className={cn("block text-primary text-sm tracking-[0.4em] font-sans transition-all duration-1000 delay-300 uppercase", isVisible ? "translate-y-0" : "translate-y-full")}>
                  Our Brand Mission
                </span>
              </div>

              {/* 主標題 */}
              <h2 className={cn("font-serif font-bold text-4xl md:text-6xl lg:text-7xl tracking-tight text-primary leading-[1.1] transition-all duration-1000 delay-500", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                匠心傳承 <br /> 
                <span className="font-normal text-foreground/90">打造木藝美學空間</span>
              </h2>

              {/* 內容段落：增加行距與分段感 */}
              <div className="space-y-8 font-sans text-lg md:text-xl leading-relaxed text-foreground/70 mt-10">
                <p className={cn("transition-all duration-1000 delay-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                  在我們看來，地板不僅是建築的基礎，更是居家溫度的來源。我們致力於將大自然的純粹美感帶入室內，讓每一寸空間都散發著優雅而溫潤的氣息。
                </p>
                <p className={cn("transition-all duration-1000 delay-800 border-l-2 border-primary/20 pl-6", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                  我們堅持嚴苛的選材與精湛的工藝，從森林源頭到最終鋪設，每一道工序都凝聚著匠人的心血。我們不僅是在製作地板，更是在為您構築一段關於家的美好回憶。
                </p>
                <p className={cn("transition-all duration-1000 delay-900", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                  憑藉對木材特性的深刻理解與數十年的產業經驗，我們不斷探索設計與機能的完美平衡，為您的專屬空間提供最契合的靈魂基石。
                </p>
              </div>

              {/* 按鈕優化 */}
              <div className={cn("pt-12 transition-all duration-1000 delay-1000", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                <Link to="/ProjectsServicesPage" className="group relative inline-flex items-center gap-4 text-primary font-serif font-bold text-2xl">
                  <span className="relative z-10 transition-colors group-hover:text-primary">
                    探索作品與服務
                  </span>
                  <MoveRight className="w-6 h-6 transition-transform group-hover:translate-x-2" />
                  
                  {/* 動態下底線 */}
                  <span className="absolute bottom-[-8px] left-0 w-full h-[1px] bg-primary/20" />
                  <span className="absolute bottom-[-8px] left-0 h-[1px] bg-primary transition-all duration-500 w-0 group-hover:w-full" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}